import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { CHOICE_REQUIRED, REQUIRED_STATUS, UNAUTHORIZED_SELECTION } from '@shared/constants/shared.constants';
import { MyAllElemCollegioSindacaleEntity } from '../../../models/interfaces/revisione.interface';
import { getGroupByCkNorme, getRevisioneCkByCkNorme } from '../../amm-controllo/common/helpers/norme-ck-lookup.helper';
import { toNum } from '@shared/helpers/math.helper';
import { CkNormeValues } from '../../amm-controllo/common/models/amm-controllo.type';
import { formPaths } from '@shared/constants/form-paths.const';
import { getAltreNotControl } from './helpers/altre-not-libri-soc-form.helper';

export const altreNotLibriSocialiOrganoContrRules: FormGroupRules = {
    my_CkLibroOrgContr: (form: FormGroup) => {
        const my_CkLibroOrgContrControl = getAltreNotControl(form, 'my_CkLibroOrgContr');
        const my_AllElemCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = getControl(
            form,
            `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`
        ).value;
        const errors = my_CkLibroOrgContrControl.errors ? my_CkLibroOrgContrControl.errors : {};

        addRemoveError(isNilOrEmpty(my_CkLibroOrgContrControl.value) && my_AllElemCollegioSindacale?.length > 0, errors, 'my_CkLibroOrgContr', {
            status: REQUIRED_STATUS,
        });

        my_CkLibroOrgContrControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkLibroOrgContrNat: (form: FormGroup) => {
        const my_CkLibroOrgContrNatControl = getAltreNotControl(form, 'my_CkLibroOrgContrNat');
        const my_AllElemCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = getControl(
            form,
            `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`
        ).value;
        const errors = my_CkLibroOrgContrNatControl.errors ? my_CkLibroOrgContrNatControl.errors : {};

        addRemoveError(isNilOrEmpty(my_CkLibroOrgContrNatControl.value) && my_AllElemCollegioSindacale?.length > 0, errors, 'my_CkLibroOrgContrNat', {
            status: REQUIRED_STATUS,
            message: CHOICE_REQUIRED,
        });

        my_CkLibroOrgContrNatControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_NoteLibroOrgContr: (form: FormGroup) => {
        const my_NoteLibroOrgContrControl = getAltreNotControl(form, 'my_NoteLibroOrgContr');
        const my_CkLibroOrgContr: string = getAltreNotControl(form, 'my_CkLibroOrgContr').value;
        const my_CkLibroOrgContrNat: string = getAltreNotControl(form, 'my_CkLibroOrgContrNat').value;
        const my_AllElemCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = getControl(
            form,
            `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`
        ).value;
        const showError =
            isNilOrEmpty(my_NoteLibroOrgContrControl.value) && (my_CkLibroOrgContrNat === '0' || my_CkLibroOrgContr === '0') && my_AllElemCollegioSindacale?.length > 0;
        const errors = my_NoteLibroOrgContrControl.errors ? my_NoteLibroOrgContrControl.errors : {};

        addRemoveError(showError, errors, 'my_NoteLibroOrgContr', {
            status: REQUIRED_STATUS,
        });

        my_NoteLibroOrgContrControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkLibroContrCont: (form: FormGroup) => {
        const amministrazioneControllo = getGroup(form, formPaths.ammControllo);
        const my_CkLibroContrContControl = getAltreNotControl(form, 'my_CkLibroContrCont');
        const my_CkNorme: CkNormeValues = getControl(amministrazioneControllo, 'my_CkNorme').value;
        const errors = my_CkLibroContrContControl.errors ? my_CkLibroContrContControl.errors : {};

        if (!isNilOrEmpty(my_CkNorme)) {
            const ckTarget = toNum(getControl(amministrazioneControllo, `${getGroupByCkNorme(my_CkNorme)}.${getRevisioneCkByCkNorme(my_CkNorme)}`).value);
            const showError = isNilOrEmpty(my_CkLibroContrContControl.value) && ckTarget !== 0 && ckTarget < 4;
            addRemoveError(showError, errors, 'my_CkLibroContrCont', {
                status: REQUIRED_STATUS,
            });
        }

        my_CkLibroContrContControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_NoteLibroContrCont: (form: FormGroup) => {
        const my_NoteLibroContrContControl = getAltreNotControl(form, 'my_NoteLibroContrCont');
        const my_CkLibroContrCont: string = getAltreNotControl(form, 'my_CkLibroContrCont').value;
        const showError = isNilOrEmpty(my_NoteLibroContrContControl.value) && my_CkLibroContrCont === '1';
        const errors = my_NoteLibroContrContControl.errors ? my_NoteLibroContrContControl.errors : {};

        addRemoveError(showError, errors, 'my_NoteLibroContrCont', {
            status: REQUIRED_STATUS,
        });

        my_NoteLibroContrContControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkVerbConoscSoci: (form: FormGroup) => {
        const my_CkVerbConoscSociControl = getAltreNotControl(form, 'my_CkVerbConoscSoci');
        const my_DataUltRev: string = getControl(form, `${formPaths.datiIdentificativi}.my_DataUltRev`).value;
        const showUnauthorizedError = toNum(my_CkVerbConoscSociControl.value) === 1 && isNilOrEmpty(my_DataUltRev);
        const errors = my_CkVerbConoscSociControl.errors ? my_CkVerbConoscSociControl.errors : {};

        addRemoveError(showUnauthorizedError, errors, 'my_CkVerbConoscSoci', {
            status: UNAUTHORIZED_SELECTION,
            message: 'Nel verbale non è presente la data di ultima revisione',
        });

        addRemoveError(isNilOrEmpty(my_CkVerbConoscSociControl.value), errors, 'my_CkVerbConoscSoci', {
            status: REQUIRED_STATUS,
        });

        my_CkVerbConoscSociControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
